import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import {ContestStatus, RequestState} from "data/enums";
import {AnalyticService} from "data/utils/track_analytics";
import type {IUserStore} from "data/stores/user/user.store";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IContest, IContestsStore} from "data/stores/contests/contests.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IStaticContentStore, TArticle} from "data/stores/static_content/static_content.store";
import type {IGetPredictionsParams} from "data/providers/api/predictions.api.provider";
import {createFanHubEvent, EventGUID} from "data/utils/analytics";

export interface IResultsController extends ViewController {
	readonly i18n: ILocalizationStore;

	// get isAuthorized(): boolean;
	get isRequestSuccess(): boolean;
	get activeRound(): IRound | undefined;
	get currentRound(): IRound | undefined;
	get availableRounds(): IRound[];
	get matchesList(): IContest[];
	get selectedIndex(): number;
	get faqContent(): TArticle[] | undefined;

	setSelectedRoundId: (roundId: number) => void;
}

@injectable()
export class ResultsController implements IResultsController {
	@observable _requestState: RequestState = RequestState.IDLE;
	private _isRoundChanged = false;
	get isRequestSuccess() {
		return this._requestState === RequestState.SUCCESS;
	}

	get activeRound() {
		return this._roundsStore.activeRound;
	}

	get currentRound() {
		return this._roundsStore.currentRound;
	}

	get availableRounds() {
		return this._roundsStore.availableRounds;
	}

	get selectedIndex() {
		return this._roundsStore.selectedIndex;
	}

	get matchesList() {
		const currentRound = this._roundsStore.currentRound;

		if (!currentRound) return [];

		return this._contestsStore.list.filter(
			(contest) =>
				contest.roundId === currentRound.id && contest.status === ContestStatus.RESULTED
		);
	}

	get faqContent() {
		return this._staticContentStore.articles;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.StaticContentStore)
		private readonly _staticContentStore: IStaticContentStore
	) {
		makeAutoObservable(this);
	}

	@action private setRequestState = (value: RequestState) => {
		this._requestState = value;
	};

	setSelectedRoundId = (roundId: number) => {
		if (this._roundsStore.selectedRoundId && this._roundsStore.selectedRoundId !== roundId) {
			createFanHubEvent(EventGUID.ResultsScheduleNavigationClick);
		}
		this._roundsStore.setSelectedRoundId(roundId);
	};

	onChange(param: void) {
		return;
	}

	dispose() {
		return;
	}

	async init(param: void) {
		this.setRequestState(RequestState.PENDING);

		AnalyticService.trackState({
			"page.legacyName": "Major League Baseball: Player of the Game",
		});

		await Promise.all([
			this._roundsStore.fetchRounds(),
			this._contestsStore.fetchContest(),
			this._playersStore.fetchPlayers(),
			this._squadsStore.fetchSquads(),
			this._staticContentStore.fetchStaticContent(),
		])
			.then(() => {
				const params: IGetPredictionsParams = {};

				if (!this._userStore.isAuthorized) {
					params.guestUid = this._userStore.guestUid;
				}

				void this._predictionsStore.fetchAllPredictions(params);
			})
			.then(() => {
				if (this.activeRound) {
					this.setSelectedRoundId(this.activeRound.id);
				}
			})
			.finally(() => this.setRequestState(RequestState.SUCCESS));
	}
}
